import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  MdArrowBack, MdNavigateBefore, MdNavigateNext, MdCheck, MdUndo,
  MdComment, MdExpandMore, MdExpandLess, MdAutoFixHigh
} from 'react-icons/md';
import ReactMarkdown from 'react-markdown';
import remarkMath from 'remark-math';
import rehypeKatex from 'rehype-katex';
import rehypeRaw from 'rehype-raw';
import 'katex/dist/katex.min.css';
import {
  getRubricsByAssignment,
  getAppliedRubricItems,
  applyRubricItem,
  removeAppliedRubricItem,
  updateAppliedRubricItem,
  calculateSubmissionScore,
  finalizeGrade,
  autoGradeSubmission,
  getFeedback,
  addFeedback,
  getStudentSubmissionsForAssignment
} from '../../utils/api/AssignmentsApi';

// ==================== Submission Content Viewer ====================

const DocumentViewer = ({ content }) => (
  <div className="prose prose-sm max-w-none p-6">
    <ReactMarkdown
      remarkPlugins={[remarkMath]}
      rehypePlugins={[rehypeRaw, rehypeKatex]}
    >
      {content || '*No content submitted*'}
    </ReactMarkdown>
  </div>
);

const ERDViewer = ({ svgContent, notation }) => (
  <div className="p-6 space-y-4">
    {svgContent && (
      <div className="bg-white rounded-lg border border-gray-200 p-4 overflow-auto">
        <div dangerouslySetInnerHTML={{ __html: svgContent }} />
      </div>
    )}
    {notation && (
      <div>
        <h4 className="text-xs font-semibold text-gray-500 uppercase mb-2">ERD Notation</h4>
        <pre className="bg-gray-50 border border-gray-200 rounded-lg p-4 text-sm font-mono whitespace-pre-wrap overflow-x-auto">
          {notation}
        </pre>
      </div>
    )}
    {!svgContent && !notation && (
      <p className="text-gray-400 text-sm italic">No ERD submitted</p>
    )}
  </div>
);

const CodeViewer = ({ code, language }) => (
  <div className="p-4">
    <pre className="bg-gray-900 text-gray-100 rounded-lg p-4 text-sm font-mono whitespace-pre-wrap overflow-x-auto">
      <code>{code || '# No code submitted'}</code>
    </pre>
  </div>
);

const SubmissionViewer = ({ submission, assignment }) => {
  const components = assignment.components || [];
  const [activeTab, setActiveTab] = useState(0);

  const content = submission.content || {};
  const componentSubs = submission.component_submissions || [];

  // If multi-component, show tabs
  if (components.length > 1) {
    return (
      <div className="h-full flex flex-col">
        <div className="flex border-b border-gray-200 bg-gray-50 px-4 flex-shrink-0">
          {components.sort((a, b) => a.order - b.order).map((comp, idx) => {
            const color = comp.type === 'document' ? 'blue' : comp.type === 'erd' ? 'purple' : 'green';
            return (
              <button
                key={comp.component_id || idx}
                onClick={() => setActiveTab(idx)}
                className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === idx
                    ? `border-${color}-500 text-${color}-700`
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                {comp.title}
              </button>
            );
          })}
        </div>
        <div className="flex-1 min-h-0 overflow-y-auto">
          {renderComponentContent(components[activeTab], componentSubs, content)}
        </div>
      </div>
    );
  }

  // Single component or legacy
  if (components.length === 1) {
    return (
      <div className="h-full overflow-y-auto">
        {renderComponentContent(components[0], componentSubs, content)}
      </div>
    );
  }

  // Legacy single-type submission
  return (
    <div className="h-full overflow-y-auto">
      {submission.submission_type === 'document' && <DocumentViewer content={content.markdown || content.text || ''} />}
      {submission.submission_type === 'erd' && <ERDViewer svgContent={content.svg} notation={content.notation} />}
      {submission.submission_type === 'code' && <CodeViewer code={content.code || ''} language={content.language} />}
      {!submission.submission_type && <p className="p-6 text-gray-400 text-sm">No submission content</p>}
    </div>
  );
};

const renderComponentContent = (component, componentSubs, content) => {
  if (!component) return null;
  const compSub = componentSubs.find(cs => cs.component_type === component.type || cs.component_id === component.component_id);
  const compContent = compSub?.content || content || {};

  switch (component.type) {
    case 'document':
      return <DocumentViewer content={compContent.markdown || compContent.text || ''} />;
    case 'erd':
      return <ERDViewer svgContent={compContent.svg} notation={compContent.notation} />;
    case 'code':
      return <CodeViewer code={compContent.code || ''} language={compContent.language} />;
    default:
      return <p className="p-6 text-gray-400 text-sm">Unknown component type: {component.type}</p>;
  }
};

// ==================== Rubric Panel ====================

const RubricItemRow = ({ item, rubricId, scoringMode, applied, onToggle, onUpdateFeedback }) => {
  const [showFeedback, setShowFeedback] = useState(false);
  const [feedback, setFeedback] = useState(applied?.feedback || '');
  const isApplied = !!applied;
  const isTiered = item.grading_type === 'tiered' && (item.point_tiers || []).length > 0;

  const pointDisplay = scoringMode === 'negative'
    ? `-${item.max_points}`
    : `+${item.max_points}`;

  const pointColor = scoringMode === 'negative'
    ? 'text-red-600'
    : 'text-green-600';

  const handleToggle = (tier = null) => {
    const points = tier ? tier.points : (scoringMode === 'negative' ? 0 : item.max_points);
    onToggle(item.item_id, rubricId, item.max_points, points, tier);
  };

  const handleSaveFeedback = () => {
    if (applied) {
      onUpdateFeedback(applied.applied_rubric_id, feedback);
    }
    setShowFeedback(false);
  };

  const linkedCount = (item.linked_questions || []).length;

  return (
    <div className={`border rounded-lg transition-all ${
      isApplied ? 'border-blue-300 bg-blue-50' : 'border-gray-200 bg-white hover:border-gray-300'
    }`}>
      <div
        className="flex items-start gap-3 p-3 cursor-pointer"
        onClick={() => !isTiered && handleToggle()}
      >
        {/* Checkbox / Applied indicator */}
        <div className={`w-5 h-5 rounded border-2 flex-shrink-0 mt-0.5 flex items-center justify-center ${
          isApplied ? 'bg-blue-600 border-blue-600' : 'border-gray-300'
        }`}>
          {isApplied && <MdCheck className="text-white text-sm" />}
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-gray-900">{item.title || 'Untitled'}</span>
            {linkedCount > 0 && (
              <span className="px-1.5 py-0.5 bg-purple-100 text-purple-700 rounded text-xs font-medium">
                {linkedCount} Q
              </span>
            )}
          </div>
          {item.description && (
            <p className="text-xs text-gray-500 mt-0.5 m-0">{item.description}</p>
          )}
        </div>

        <span className={`text-sm font-bold flex-shrink-0 ${isApplied ? 'text-blue-700' : pointColor}`}>
          {isApplied ? `${applied.points_awarded}/${item.max_points}` : pointDisplay}
        </span>
      </div>

      {/* Tiered options */}
      {isTiered && (
        <div className="px-3 pb-3 pl-11 space-y-1">
          {item.point_tiers.map((tier, idx) => (
            <button
              key={idx}
              onClick={() => handleToggle(tier)}
              className={`w-full text-left px-3 py-1.5 rounded text-xs flex items-center gap-2 transition-colors ${
                applied?.selected_tier === idx
                  ? 'bg-blue-100 text-blue-800 font-medium'
                  : 'bg-gray-50 text-gray-600 hover:bg-gray-100'
              }`}
            >
              <span className="font-semibold">{tier.points}pts</span>
              {tier.label && <span className="font-medium">({tier.label})</span>}
              {tier.criteria && <span className="text-gray-500">{tier.criteria}</span>}
            </button>
          ))}
        </div>
      )}

      {/* Feedback toggle */}
      {isApplied && (
        <div className="border-t border-blue-200">
          <button
            onClick={(e) => { e.stopPropagation(); setShowFeedback(!showFeedback); }}
            className="w-full px-3 py-1.5 flex items-center gap-1 text-xs text-blue-600 hover:bg-blue-100 transition-colors"
          >
            <MdComment className="text-xs" />
            {feedback ? 'Edit feedback' : 'Add feedback'}
            {showFeedback ? <MdExpandLess /> : <MdExpandMore />}
          </button>
          {showFeedback && (
            <div className="px-3 pb-3">
              <textarea
                value={feedback}
                onChange={(e) => setFeedback(e.target.value)}
                placeholder="Feedback for this item..."
                className="w-full px-2 py-1.5 border border-gray-300 rounded text-xs resize-none focus:outline-none focus:ring-1 focus:ring-blue-500"
                rows={2}
              />
              <button
                onClick={handleSaveFeedback}
                className="mt-1 px-2 py-1 bg-blue-600 text-white rounded text-xs hover:bg-blue-700"
              >
                Save
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ==================== Main GradingView ====================

const GradingView = ({
  submission: initialSubmission,
  assignment,
  classContext,
  submissions = [], // kept for backward compat but not used for navigation
  onBack,
  onNavigate // (submission) => navigate to different submission
}) => {
  const [rubrics, setRubrics] = useState([]);
  const [appliedItems, setAppliedItems] = useState([]);
  const [score, setScore] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [successMsg, setSuccessMsg] = useState(null);
  const [activeRubricTab, setActiveRubricTab] = useState(0);
  const [overallFeedback, setOverallFeedback] = useState('');
  const [feedbackSaved, setFeedbackSaved] = useState(true);
  const feedbackTimerRef = React.useRef(null);

  // Student's submissions for this assignment (for per-student navigation)
  const [studentSubmissions, setStudentSubmissions] = useState([]);
  const [currentSubmission, setCurrentSubmission] = useState(initialSubmission);

  const submission = currentSubmission;
  const components = assignment.components || [];

  // Find current index within this student's submissions
  const currentIdx = studentSubmissions.findIndex(s => s.submission_id === submission.submission_id);
  const totalAttempts = studentSubmissions.length;

  // Load all submissions for this student on mount
  useEffect(() => {
    if (initialSubmission?.student_id) {
      getStudentSubmissionsForAssignment(initialSubmission.student_id, assignment.assignment_id, classContext)
        .then(subs => {
          // Sort by attempt_number ascending (oldest first)
          const sorted = subs.sort((a, b) => (a.attempt_number || 0) - (b.attempt_number || 0));
          setStudentSubmissions(sorted);
          // Default to the latest (last) submission
          if (sorted.length > 0) {
            setCurrentSubmission(sorted[sorted.length - 1]);
          }
        })
        .catch(err => console.error('Error loading student submissions:', err));
    }
  }, [initialSubmission?.student_id, assignment.assignment_id]);

  useEffect(() => {
    loadGradingData();
  }, [submission.submission_id]);

  const loadGradingData = async () => {
    try {
      setLoading(true);
      setError(null);

      const [rubricsResult, appliedResult, scoreResult, feedbackResult] = await Promise.all([
        getRubricsByAssignment(assignment.assignment_id, classContext),
        getAppliedRubricItems(submission.submission_id, classContext),
        calculateSubmissionScore(submission.submission_id, classContext),
        getFeedback(submission.submission_id, 'overall', classContext)
      ]);

      setRubrics(rubricsResult.rubrics || []);
      setAppliedItems(appliedResult.applied_rubric_items || []);
      setScore(scoreResult.score || scoreResult);
      const existingFeedback = (feedbackResult.feedback || []).find(f => f.feedback_type === 'overall');
      setOverallFeedback(existingFeedback?.content || '');
      setFeedbackSaved(true);
    } catch (err) {
      console.error('Error loading grading data:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleItem = async (itemId, rubricId, maxPoints, pointsAwarded, tier = null) => {
    try {
      setSaving(true);
      setError(null);

      // Check if already applied
      const existing = appliedItems.find(a => a.rubric_item_id === itemId);

      if (existing) {
        // Unapply — delete the applied item
        await removeAppliedRubricItem(existing.applied_rubric_id, classContext);
      } else {
        // Apply
        await applyRubricItem({
          submission_id: submission.submission_id,
          rubric_id: rubricId,
          rubric_item_id: itemId,
          max_points: maxPoints,
          points_awarded: pointsAwarded,
          selected_tier: tier ? tier.points : null,
          graded_by: classContext.username
        }, classContext);
      }

      // Refresh applied items and score
      const [appliedResult, scoreResult] = await Promise.all([
        getAppliedRubricItems(submission.submission_id, classContext),
        calculateSubmissionScore(submission.submission_id, classContext)
      ]);
      setAppliedItems(appliedResult.applied_rubric_items || []);
      setScore(scoreResult.score || scoreResult);
    } catch (err) {
      console.error('Error toggling rubric item:', err);
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateFeedback = async (appliedRubricId, feedback) => {
    try {
      await updateAppliedRubricItem(appliedRubricId, { feedback }, classContext);
      const appliedResult = await getAppliedRubricItems(submission.submission_id, classContext);
      setAppliedItems(appliedResult.applied_rubric_items || []);
    } catch (err) {
      console.error('Error updating feedback:', err);
    }
  };

  const handleAutoGrade = async () => {
    try {
      setSaving(true);
      await autoGradeSubmission(submission.submission_id, classContext);
      await loadGradingData();
      setSuccessMsg('Auto-graded successfully');
      setTimeout(() => setSuccessMsg(null), 2000);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleSaveOverallFeedback = async () => {
    if (feedbackSaved) return;
    try {
      await addFeedback({
        submission_id: submission.submission_id,
        feedback_type: 'overall',
        content: overallFeedback,
        is_visible_to_student: true
      }, classContext);
      setFeedbackSaved(true);
    } catch (err) {
      console.error('Error saving feedback:', err);
    }
  };

  const handleFeedbackChange = (value) => {
    setOverallFeedback(value);
    setFeedbackSaved(false);
    // Debounced auto-save
    if (feedbackTimerRef.current) clearTimeout(feedbackTimerRef.current);
    feedbackTimerRef.current = setTimeout(() => {
      handleSaveOverallFeedback();
    }, 2000);
  };

  const handleFinalize = async () => {
    if (!score) return;
    if (!window.confirm(`Finalize grade for ${submission.student_id}? Score: ${score.total_points_awarded}/${score.total_max_points}`)) return;

    try {
      setSaving(true);
      // Save any unsaved feedback first
      if (!feedbackSaved && overallFeedback) {
        await addFeedback({
          submission_id: submission.submission_id,
          feedback_type: 'overall',
          content: overallFeedback,
          is_visible_to_student: true
        }, classContext);
      }
      await finalizeGrade(submission.submission_id, classContext);
      setSuccessMsg('Grade finalized');
      setTimeout(() => setSuccessMsg(null), 2000);
      await loadGradingData();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handlePrev = () => {
    if (currentIdx > 0) {
      setCurrentSubmission(studentSubmissions[currentIdx - 1]);
    }
  };

  const handleNext = () => {
    if (currentIdx < studentSubmissions.length - 1) {
      setCurrentSubmission(studentSubmissions[currentIdx + 1]);
    }
  };

  // Group rubrics by component
  const rubricsByComponent = useMemo(() => {
    if (components.length === 0) return [{ label: 'Rubric', rubrics: rubrics }];
    return components.map(comp => ({
      label: comp.title,
      type: comp.type,
      component_id: comp.component_id,
      rubrics: rubrics.filter(r => r.component_id === comp.component_id)
    }));
  }, [rubrics, components]);

  const hasLinkedQuestions = rubrics.some(r =>
    (r.items || []).some(item => (item.linked_questions || []).length > 0)
  );

  return (
    <div className="h-full flex flex-col bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-4 py-3 flex items-center gap-3 flex-shrink-0">
        <button onClick={onBack} className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-600">
          <MdArrowBack className="text-lg" />
        </button>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="font-semibold text-gray-900 text-sm">{submission.student_id}</span>
            <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
              submission.status === 'graded' ? 'bg-green-100 text-green-700' :
              submission.status === 'returned' ? 'bg-purple-100 text-purple-700' :
              'bg-blue-100 text-blue-700'
            }`}>
              {submission.status}
            </span>
            {submission.is_late && (
              <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-orange-100 text-orange-700">Late</span>
            )}
          </div>
        </div>

        {/* Score */}
        {score && (
          <div className="text-right mr-2">
            <div className="text-lg font-bold text-gray-900">
              {score.total_points_awarded}/{score.total_max_points}
            </div>
            <div className="text-xs text-gray-500">{score.score_percentage}%</div>
          </div>
        )}

        {/* Navigation through student's submissions */}
        {totalAttempts > 1 && (
          <div className="flex items-center gap-1 border-l border-gray-200 pl-3">
            <button
              onClick={handlePrev}
              disabled={currentIdx <= 0}
              className="p-1.5 rounded hover:bg-gray-100 text-gray-600 disabled:text-gray-300 disabled:hover:bg-transparent"
            >
              <MdNavigateBefore className="text-lg" />
            </button>
            <span className="text-xs text-gray-500 min-w-[80px] text-center whitespace-nowrap">
              Submission {currentIdx + 1}/{totalAttempts}
            </span>
            <button
              onClick={handleNext}
              disabled={currentIdx >= totalAttempts - 1}
              className="p-1.5 rounded hover:bg-gray-100 text-gray-600 disabled:text-gray-300 disabled:hover:bg-transparent"
            >
              <MdNavigateNext className="text-lg" />
            </button>
          </div>
        )}
      </div>

      {/* Messages */}
      {error && (
        <div className="mx-4 mt-2 bg-red-50 border border-red-200 rounded-lg px-3 py-2 text-red-800 text-xs flex-shrink-0">
          {error}
          <button onClick={() => setError(null)} className="ml-2 font-bold">×</button>
        </div>
      )}
      {successMsg && (
        <div className="mx-4 mt-2 bg-green-50 border border-green-200 rounded-lg px-3 py-2 text-green-800 text-xs flex-shrink-0">
          {successMsg}
        </div>
      )}

      {/* Main split pane */}
      <div className="flex-1 min-h-0 flex">
        {/* Left: Student Submission */}
        <div className="flex-1 min-w-0 border-r border-gray-200 bg-white overflow-hidden">
          <div className="h-full overflow-y-auto">
            <SubmissionViewer submission={submission} assignment={assignment} />
          </div>
        </div>

        {/* Right: Rubric Panel */}
        <div className="w-[400px] flex-shrink-0 flex flex-col bg-gray-50">
          {loading ? (
            <div className="flex items-center justify-center h-32 text-gray-500 text-sm">Loading rubric...</div>
          ) : (
            <>
              {/* Rubric component tabs */}
              {rubricsByComponent.length > 1 && (
                <div className="flex border-b border-gray-200 bg-white px-2 flex-shrink-0 overflow-x-auto">
                  {rubricsByComponent.map((group, idx) => (
                    <button
                      key={idx}
                      onClick={() => setActiveRubricTab(idx)}
                      className={`px-3 py-2 text-xs font-medium border-b-2 whitespace-nowrap transition-colors ${
                        activeRubricTab === idx
                          ? 'border-blue-500 text-blue-700'
                          : 'border-transparent text-gray-500 hover:text-gray-700'
                      }`}
                    >
                      {group.label}
                    </button>
                  ))}
                </div>
              )}

              {/* Rubric items */}
              <div className="flex-1 min-h-0 overflow-y-auto p-3 space-y-2">
                {(() => {
                  const group = rubricsByComponent[activeRubricTab];
                  if (!group || group.rubrics.length === 0) {
                    return <p className="text-center text-gray-400 text-sm py-8">No rubric for this component</p>;
                  }

                  return group.rubrics.map(rubric => (
                    <div key={rubric.rubric_id}>
                      {group.rubrics.length > 1 && (
                        <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">{rubric.title}</h4>
                      )}
                      {(rubric.items || []).filter(i => i.is_active !== false).map(item => (
                        <RubricItemRow
                          key={item.item_id}
                          item={item}
                          rubricId={rubric.rubric_id}
                          scoringMode={rubric.scoring_mode || 'positive'}
                          applied={appliedItems.find(a => a.rubric_item_id === item.item_id)}
                          onToggle={handleToggleItem}
                          onUpdateFeedback={handleUpdateFeedback}
                        />
                      ))}
                    </div>
                  ));
                })()}
              </div>

              {/* Overall Feedback */}
              <div className="border-t border-gray-200 bg-white p-3 flex-shrink-0">
                <div className="flex items-center justify-between mb-2">
                  <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide m-0">Overall Feedback</h4>
                  {!feedbackSaved && (
                    <span className="text-xs text-yellow-600">Unsaved</span>
                  )}
                </div>
                <textarea
                  value={overallFeedback}
                  onChange={(e) => handleFeedbackChange(e.target.value)}
                  onBlur={handleSaveOverallFeedback}
                  placeholder="Write overall feedback for this student..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm resize-none focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                  rows={3}
                />
              </div>

              {/* Bottom actions */}
              <div className="border-t border-gray-200 bg-white p-3 flex-shrink-0">
                <div className="flex items-center gap-2">
                  {hasLinkedQuestions && (
                    <button
                      onClick={handleAutoGrade}
                      disabled={saving}
                      className="flex items-center gap-1 px-3 py-2 text-xs font-medium text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg hover:bg-emerald-100 disabled:opacity-50"
                    >
                      <MdAutoFixHigh className="text-sm" />
                      Auto-Grade
                    </button>
                  )}
                  <div className="flex-1" />
                  <button
                    onClick={handleFinalize}
                    disabled={saving || !score || (score.total_max_points === 0)}
                    className="flex items-center gap-1 px-4 py-2 text-xs font-semibold text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50"
                  >
                    <MdCheck className="text-sm" />
                    Finalize Grade
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default GradingView;
